// 09-10 (maqueta aprobada «lo que dictas no se pierde»): el borrador del compositor, por agente, en el
// almacenamiento del navegador. Antes vivía en memoria (un Map): recargar, que iOS cerrara la PWA en segundo
// plano o un corte lo borraba — y un dictado largo de Pau se perdía entero (cursor-ux-mirror §2.2).
//
// Escribe con un pequeño retardo (no en cada letra) y vuelca lo pendiente al cambiar de chat, al ocultarse la
// página (pasar a otra app) y al cerrarse. Sin almacenamiento (modo privado, cuota llena) sigue funcionando en
// memoria, sin avisar: el borrador no es razón para molestar a Pau.
import { useEffect, useRef, useState } from 'react';

export const PREFIJO_BORRADOR = 'oficina:borrador:';
export const RETARDO_BORRADOR_MS = 400;
/** Tope por borrador: un pegado enorme no llena el almacenamiento de todo el panel. */
const MAX_CARACTERES = 100_000;

// Sin almacenamiento (modo privado, cuota), los borradores viven aquí mientras dure la visita: cambiar de chat
// sigue sin perderlos, como antes. Solo guarda los que el almacenamiento rechazó (y manda sobre él).
const enMemoria = new Map<string, string>();

export function leerBorrador(agentId: string): string {
  const deMemoria = enMemoria.get(agentId);
  if (deMemoria !== undefined) return deMemoria;
  try {
    return localStorage.getItem(PREFIJO_BORRADOR + agentId) ?? '';
  } catch {
    return '';
  }
}

export function guardarBorrador(agentId: string, texto: string): void {
  try {
    if (texto) localStorage.setItem(PREFIJO_BORRADOR + agentId, texto.slice(0, MAX_CARACTERES));
    else localStorage.removeItem(PREFIJO_BORRADOR + agentId);
    enMemoria.delete(agentId);
  } catch {
    if (texto) enMemoria.set(agentId, texto);
    else enMemoria.delete(agentId);
  }
}

/** El texto del compositor de un agente, recuperado al montar y guardado mientras se escribe. */
export function useBorrador(agentId: string): [string, (texto: string) => void] {
  const [texto, setTexto] = useState(() => leerBorrador(agentId));
  // Lo escrito que aún no ha llegado al almacenamiento (null = nada pendiente).
  const pendiente = useRef<string | null>(null);

  useEffect(() => {
    pendiente.current = texto;
    const id = setTimeout(() => {
      guardarBorrador(agentId, texto);
      pendiente.current = null;
    }, RETARDO_BORRADOR_MS);
    return () => clearTimeout(id);
  }, [agentId, texto]);

  useEffect(() => {
    const volcar = () => {
      if (pendiente.current === null) return;
      guardarBorrador(agentId, pendiente.current);
      pendiente.current = null;
    };
    const alOcultar = () => {
      if (document.visibilityState === 'hidden') volcar();
    };
    window.addEventListener('pagehide', volcar);
    document.addEventListener('visibilitychange', alOcultar);
    return () => {
      window.removeEventListener('pagehide', volcar);
      document.removeEventListener('visibilitychange', alOcultar);
      volcar();
    };
  }, [agentId]);

  return [texto, setTexto];
}
